import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  description: string;
  createdAt: string;
  status: string;
  orderId?: string;
  source: string;
}

interface TransactionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  userId?: string;
}

export default function TransactionHistory({
  isOpen,
  onClose,
  companyId,
  userId
}: TransactionHistoryProps) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [firstDoc, setFirstDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 10;

  const loadTransactions = useCallback(async (loadMore = false) => {
    try {
      setLoading(true);
      setError('');
      
      // Buscar transações da coleção walletTransactions
      const walletTransactionsRef = collection(db, 'walletTransactions');
      let walletQueryConstraints: any[] = [
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      ];

      // Se tiver userId, busca também por ele
      if (userId) {
        const walletTransactionsUserRef = collection(db, 'walletTransactions');
        const walletUserQueryConstraints = [
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        ];

        if (loadMore && lastDoc?.walletUser) {
          walletUserQueryConstraints.push(startAfter(lastDoc.walletUser));
        }
        walletUserQueryConstraints.push(limit(ITEMS_PER_PAGE));

        // Buscar transações por companyId e userId em paralelo
        const [companySnapshot, userSnapshot] = await Promise.all([
          getDocs(query(walletTransactionsRef, ...walletQueryConstraints)),
          getDocs(query(walletTransactionsUserRef, ...walletUserQueryConstraints))
        ]);

        // Combinar resultados únicos (evitar duplicatas)
        const uniqueTransactions = new Map();
        
        [...companySnapshot.docs, ...userSnapshot.docs].forEach(doc => {
          if (!uniqueTransactions.has(doc.id)) {
            uniqueTransactions.set(doc.id, {
              id: doc.id,
              source: 'wallet',
              type: doc.data().type || 'manual',
              amount: doc.data().amount,
              description: doc.data().description,
              status: doc.data().status,
              createdAt: doc.data().createdAt instanceof Date ? doc.data().createdAt.toISOString() : 
                        typeof doc.data().createdAt === 'object' ? doc.data().createdAt.toDate().toISOString() : 
                        doc.data().createdAt,
              orderId: doc.data().orderId
            });
          }
        });

        const walletTransactions = Array.from(uniqueTransactions.values());

        // Buscar transações normais
        const transactionsRef = collection(db, 'transactions');
        let transQueryConstraints: any[] = [
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        ];

        if (loadMore && lastDoc?.trans) {
          transQueryConstraints.push(startAfter(lastDoc.trans));
        }
        transQueryConstraints.push(limit(ITEMS_PER_PAGE));

        const transSnapshot = await getDocs(query(transactionsRef, ...transQueryConstraints));

        const normalTransactions = transSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            source: 'transaction',
            type: data.type || 'transaction',
            amount: data.amount,
            description: data.description,
            status: data.status,
            createdAt: data.createdAt instanceof Date ? data.createdAt.toISOString() : 
                      typeof data.createdAt === 'object' ? data.createdAt.toDate().toISOString() : 
                      data.createdAt,
            orderId: data.orderId
          } as Transaction;
        });

        // Combinar e ordenar todas as transações por data
        const allTransactions = [...walletTransactions, ...normalTransactions]
          .sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA;
          });

        setTransactions(prev => loadMore ? [...prev, ...allTransactions] : allTransactions);

        // Guardar último documento de cada coleção para paginação
        setLastDoc({
          wallet: companySnapshot.docs[companySnapshot.docs.length - 1] || null,
          walletUser: userSnapshot.docs[userSnapshot.docs.length - 1] || null,
          trans: transSnapshot.docs[transSnapshot.docs.length - 1] || null
        });

        // Guardar primeiro documento de cada coleção
        setFirstDoc({
          wallet: companySnapshot.docs[0] || null,
          walletUser: userSnapshot.docs[0] || null,
          trans: transSnapshot.docs[0] || null
        });

        // Atualizar flag hasMore
        setHasMore(
          companySnapshot.docs.length === ITEMS_PER_PAGE || 
          userSnapshot.docs.length === ITEMS_PER_PAGE ||
          transSnapshot.docs.length === ITEMS_PER_PAGE
        );

      } else {
        // Buscar transações da coleção walletTransactions
        const walletTransactionsRef = collection(db, 'walletTransactions');
        let walletQueryConstraints: any[] = [
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        ];

        // Buscar transações da coleção transactions
        const transactionsRef = collection(db, 'transactions');
        let transQueryConstraints: any[] = [
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        ];

        // Add pagination
        if (loadMore && lastDoc) {
          if (lastDoc.wallet) walletQueryConstraints.push(startAfter(lastDoc.wallet));
          if (lastDoc.trans) transQueryConstraints.push(startAfter(lastDoc.trans));
        }

        // Add limit para cada coleção
        walletQueryConstraints.push(limit(ITEMS_PER_PAGE));
        transQueryConstraints.push(limit(ITEMS_PER_PAGE));

        const [walletSnapshot, transSnapshot] = await Promise.all([
          getDocs(query(walletTransactionsRef, ...walletQueryConstraints)),
          getDocs(query(transactionsRef, ...transQueryConstraints))
        ]);

        // Converter documentos em transações
        const walletTransactions = walletSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            source: 'wallet',
            type: data.type || 'manual',
            amount: data.amount,
            description: data.description,
            status: data.status,
            createdAt: data.createdAt instanceof Date ? data.createdAt.toISOString() : 
                      typeof data.createdAt === 'object' ? data.createdAt.toDate().toISOString() : 
                      data.createdAt,
            orderId: data.orderId
          } as Transaction;
        });

        const normalTransactions = transSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            source: 'transaction',
            type: data.type || 'transaction',
            amount: data.amount,
            description: data.description,
            status: data.status,
            createdAt: data.createdAt instanceof Date ? data.createdAt.toISOString() : 
                      typeof data.createdAt === 'object' ? data.createdAt.toDate().toISOString() : 
                      data.createdAt,
            orderId: data.orderId
          } as Transaction;
        });

        // Combinar e ordenar todas as transações por data
        const allTransactions = [...walletTransactions, ...normalTransactions]
          .sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA;
          });

        setTransactions(prev => loadMore ? [...prev, ...allTransactions] : allTransactions);

        // Guardar último documento de cada coleção para paginação
        setLastDoc({
          wallet: walletSnapshot.docs[walletSnapshot.docs.length - 1] || null,
          trans: transSnapshot.docs[transSnapshot.docs.length - 1] || null
        });

        // Guardar primeiro documento de cada coleção
        setFirstDoc({
          wallet: walletSnapshot.docs[0] || null,
          trans: transSnapshot.docs[0] || null
        });

        // Atualizar flag hasMore
        setHasMore(
          walletSnapshot.docs.length === ITEMS_PER_PAGE || 
          transSnapshot.docs.length === ITEMS_PER_PAGE
        );
      }
    } catch (err) {
      console.error('Erro ao carregar transações:', err);
      setError('Erro ao carregar histórico de transações');
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, lastDoc]);

  useEffect(() => {
    if (isOpen) {
      loadTransactions();
    }
  }, [isOpen, loadTransactions]);

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
      loadTransactions(true);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      loadTransactions(false);
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'Concluído';
      case 'pending':
        return 'Pendente';
      case 'failed':
        return 'Falhou';
      default:
        return status;
    }
  };

  const getTransactionTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      'deposit': 'Depósito',
      'withdrawal': 'Levantamento',
      'payment': 'Pagamento',
      'refund': 'Reembolso',
      'cancellation_fee': 'Taxa de Cancelamento',
      'manual': 'Manual',
      'transaction': 'Transação'
    };
    return types[type] || type;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Data inválida';
      
      return date.toLocaleDateString('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'Data inválida';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-xl font-semibold text-gray-800">Histórico de Transações</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}

          {loading && transactions.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-lg">Nenhuma transação encontrada</p>
              <p className="text-sm text-gray-400">As suas transações aparecerão aqui</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="border-b">
                      <td className="py-4 px-4">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="py-4 px-4">
                        {transaction.description}
                        <span className="ml-2 text-xs text-gray-500">
                          ({getTransactionTypeText(transaction.type)})
                        </span>
                      </td>
                      <td className={`py-4 px-4 text-right ${
                        transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.amount > 0 ? '+' : ''}
                        €{Math.abs(transaction.amount).toFixed(2)}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          transaction.status === 'completed' 
                            ? 'bg-green-100 text-green-800'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.status === 'completed' ? 'Concluído' :
                           transaction.status === 'pending' ? 'Pendente' : 'Falhou'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer with pagination */}
        {(hasMore || currentPage > 1) && (
          <div className="border-t px-6 py-4">
            <div className="flex justify-between items-center">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft size={16} />
                Anterior
              </button>
              <button
                onClick={handleNextPage}
                disabled={!hasMore || loading}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  !hasMore || loading
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Próxima
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
